import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, IconButton,
  CircularProgress, Alert, Chip, Typography
} from '@mui/material';
import { Edit as EditIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '@vortx/shared-ui';
import { API_BASE_URL } from '../../services/api';

const SubscriptionsPage = () => {
  const { currentUser } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editDialog, setEditDialog] = useState({ open: false, subscription: null });

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/companies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch companies');

      const data = await response.json();
      setCompanies(data.companies);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const fetchSubscriptions = useCallback(async (companyId) => {
    try {
      setSubscriptionsLoading(true);
      setError(null);
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/companies/${companyId}/subscriptions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch subscriptions');

      const data = await response.json();
      setSubscriptions(data.subscriptions);
    } catch (err) {
      setError(err.message);
      setSubscriptions([]);
    } finally {
      setSubscriptionsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchSubscriptions(selectedCompanyId);
    } else {
      setSubscriptions([]);
    }
  }, [selectedCompanyId, fetchSubscriptions]);

  const handleSave = async (subscription) => {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `/api/v1/admin/companies/${selectedCompanyId}/subscriptions/${subscription.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            plan: subscription.plan,
            seat_limit: subscription.seat_limit
          })
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update subscription');
      }

      setEditDialog({ open: false, subscription: null });
      fetchSubscriptions(selectedCompanyId);
    } catch (err) {
      setError(err.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'trial': return 'info';
      case 'suspended': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getProductLabel = (product) => {
    switch (product) {
      case 'site_dna': return 'Site DNA';
      case 'flux_phd': return 'Flux PhD';
      case 'flux_ems': return 'Flux EMS';
      default: return product;
    }
  };

  const getSelectedCompanyName = () => {
    const company = companies.find(c => c.id === selectedCompanyId);
    return company?.name || selectedCompanyId;
  };

  if (loading) return <AdminLayout title="Subscriptions" requireSuperAdmin><CircularProgress /></AdminLayout>;

  return (
    <AdminLayout title="Subscription Management" requireSuperAdmin>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>Select Company</InputLabel>
          <Select
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
            label="Select Company"
          >
            <MenuItem value="">
              <em>Select a company...</em>
            </MenuItem>
            {companies.map((company) => (
              <MenuItem key={company.id} value={company.id}>
                {company.name} ({company.id})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedCompanyId && (
          <IconButton
            onClick={() => fetchSubscriptions(selectedCompanyId)}
            sx={{ ml: 1 }}
            title="Refresh subscriptions"
          >
            <RefreshIcon />
          </IconButton>
        )}
      </Box>

      {selectedCompanyId && (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Subscriptions for {getSelectedCompanyName()}
          </Typography>

          {subscriptionsLoading ? (
            <CircularProgress />
          ) : subscriptions.length === 0 ? (
            <Alert severity="info">No subscriptions found for this company.</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Seat Limit</TableCell>
                    <TableCell>Seats Used</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subscriptions.map((subscription) => (
                    <TableRow key={subscription.id}>
                      <TableCell>
                        <Chip
                          label={getProductLabel(subscription.product)}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{subscription.plan}</TableCell>
                      <TableCell>{subscription.seat_limit ?? 'Unlimited'}</TableCell>
                      <TableCell>{subscription.seats_used ?? 0}</TableCell>
                      <TableCell>
                        <Chip
                          label={subscription.status}
                          color={getStatusColor(subscription.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => setEditDialog({ open: true, subscription })}>
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <SubscriptionDialog
        open={editDialog.open}
        subscription={editDialog.subscription}
        onClose={() => setEditDialog({ open: false, subscription: null })}
        onSave={handleSave}
      />
    </AdminLayout>
  );
};

const SubscriptionDialog = ({ open, subscription, onClose, onSave }) => {
  const [form, setForm] = useState(subscription || {});

  useEffect(() => {
    setForm(subscription || {});
  }, [subscription]);

  const getProductLabel = (product) => {
    switch (product) {
      case 'site_dna': return 'Site DNA';
      case 'flux_phd': return 'Flux PhD';
      case 'flux_ems': return 'Flux EMS';
      default: return product;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Subscription - {getProductLabel(subscription?.product)}</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="normal">
          <InputLabel>Plan</InputLabel>
          <Select
            value={form.plan || ''}
            onChange={(e) => setForm({ ...form, plan: e.target.value })}
            label="Plan"
          >
            <MenuItem value="free">Free</MenuItem>
            <MenuItem value="starter">Starter</MenuItem>
            <MenuItem value="professional">Professional</MenuItem>
            <MenuItem value="enterprise">Enterprise</MenuItem>
          </Select>
        </FormControl>
        <TextField
          fullWidth
          label="Seat Limit"
          type="number"
          value={form.seat_limit ?? ''}
          onChange={(e) => setForm({ ...form, seat_limit: e.target.value ? parseInt(e.target.value, 10) : null })}
          margin="normal"
          helperText="Leave empty for unlimited seats"
          InputProps={{ inputProps: { min: 1 } }}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Status</InputLabel>
          <Select
            value={form.status || ''}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            label="Status"
            disabled
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="trial">Trial</MenuItem>
            <MenuItem value="suspended">Suspended</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Note: Status changes are managed through the subscription lifecycle and cannot be edited directly.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSave(form)} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionsPage;
